import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Trash2, Save, Send, FileText, Eye, Download, TestTube, Building2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { useToast } from '../../../components/ui/use-toast';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import PaymentTermPicker from '../../../components/shared/PaymentTermPicker';
import CustomerPicker from '../../../components/shared/CustomerPicker';
import TaxIdInput from '../../../components/admin/TaxIdInput';
import DualCurrencyPicker from '../../../components/admin/finance/DualCurrencyPicker';
import ProductPicker from '../../../components/admin/ProductPicker';
import PromoteItemToProductModal from '../../../components/admin/PromoteItemToProductModal';
import { defaultTaxIdForCountry } from '../../../config/taxIdTypes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvoiceCreate = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [generatedInvoice, setGeneratedInvoice] = useState(null);
  const [successDialog, setSuccessDialog] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [promoteIdx, setPromoteIdx] = useState(null);
  const [companySnapshot, setCompanySnapshot] = useState(null);
  const token = localStorage.getItem('admin_token');

  const [formData, setFormData] = useState({
    company_id: '',
    customer_id: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    client_website: '',
    client_address: '',
    client_trn: '',
    client_tax_id_type: 'TRN_UAE',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    currency: 'AED',
    // F-DualCurrency — optional secondary display currency + frozen rate
    display_currency: null,
    fx_rate: null,
    fx_rate_source: null,
    fx_rate_date: null,
    bank_account_id: null,        // F-BankAccountOnInvoice
    notes: '',
    terms: 'Payment due within 30 days',
    // Payment options offered on this invoice (mirrors Procurement Invoice module).
    payment_methods: ['bank_transfer'],
    payment_link_url: '',
    payment_link_provider: 'Network International',
    payment_link_label: '',
    items: [
      { description_en: '', quantity: 1, unit_price: 0, tax_rate: 5, tax_amount: 0, total: 0 }
    ]
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => { calculateTotals(); }, [formData.items]);

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${API_URL}/api/procurement/companies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setCompanies(data);
      if (data.length === 1) {
        setFormData(prev => ({ ...prev, company_id: data[0].id }));
        loadCompanySnapshot(data[0].id);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const loadCompanySnapshot = async (companyId) => {
    try {
      const response = await fetch(`${API_URL}/api/procurement/companies/${companyId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed');
      const company = await response.json();
      setCompanySnapshot(company);
      const defaultCurrency = company.currency || 'AED';
      setFormData(prev => ({ ...prev, currency: defaultCurrency }));
    } catch (error) {
      console.error('Error loading company snapshot:', error);
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to load company details' });
    }
  };

  const handleCustomerSelect = (customer) => {
    if (!customer) return;
    const addr = customer.address || {};
    const addressStr = [addr.street, addr.city, addr.country, addr.postal_code]
      .filter(Boolean)
      .join(', ');
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id || '',
      client_name: customer.company_name || customer.name || '',
      client_email: customer.email || '',
      client_phone: customer.phone || '',
      client_website: customer.website || '',
      client_address: addressStr,
      client_trn: customer.trn || '',
      client_tax_id_type: customer.tax_id_type
        || defaultTaxIdForCountry(customer.address?.country)
    }));
    toast({
      title: 'Customer autofilled',
      description: `${customer.company_name || customer.name} — all fields pre-loaded.`,
      duration: 2500,
    });
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const taxTotal = formData.items.reduce((sum, item) => sum + item.tax_amount, 0);
    setFormData(prev => ({ ...prev, subtotal, tax_total: taxTotal, total: subtotal + taxTotal }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'description_en' ? value : parseFloat(value) || 0;
    const item = newItems[index];
    const lineSubtotal = item.quantity * item.unit_price;
    item.tax_amount = lineSubtotal * (item.tax_rate / 100);
    item.total = lineSubtotal + item.tax_amount;
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  // F-UnifiedProducts — auto-fill line from Master Product Registry.
  // User may still edit every field manually afterwards.
  const handleItemProductPick = (index, product) => {
    if (!product) return;
    const newItems = [...formData.items];
    const item = newItems[index];
    item.product_id = product.id;
    item.product_sku = product.sku;
    if (!item.description_en || item.description_en.trim() === '') {
      item.description_en = product.name || '';
    }
    if (product.default_price != null && !item.unit_price) {
      item.unit_price = parseFloat(product.default_price) || 0;
    }
    if (product.default_tax_rate != null) {
      item.tax_rate = parseFloat(product.default_tax_rate) || item.tax_rate;
    }
    const lineSubtotal = (item.quantity || 0) * (item.unit_price || 0);
    item.tax_amount = lineSubtotal * ((item.tax_rate || 0) / 100);
    item.total = lineSubtotal + item.tax_amount;
    setFormData(prev => ({ ...prev, items: newItems }));
    const prodCur = (product.default_currency || '').toUpperCase();
    const invCur = (formData.currency || '').toUpperCase();
    if (prodCur && invCur && prodCur !== invCur) {
      toast({
        variant: 'destructive',
        title: `Price is in ${prodCur}, invoice is in ${invCur}`,
        description: `No currency conversion was applied — adjust the unit price or set the invoice currency to ${prodCur}.`,
        duration: 6000,
      });
    } else {
      toast({
        title: 'Product loaded',
        description: `${product.sku} — ${product.name} (editable below).`,
        duration: 2500,
      });
    }
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description_en: '', quantity: 1, unit_price: 0, tax_rate: 5, tax_amount: 0, total: 0 }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
    }
  };

  const saveDraft = async () => {
    if (!formData.company_id) {
      toast({ variant: 'destructive', title: 'Validation Error', description: 'Please select an Issuer Company.' });
      return;
    }
    if (!formData.client_name) {
      toast({ variant: 'destructive', title: 'Validation Error', description: 'Client name is required.' });
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/finance/invoices/create-simple`,
        { ...formData, status: 'draft' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast({ title: 'Success', description: 'Draft saved successfully.' });
        navigate('/admin/finance/invoices');
      }
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: error.response?.data?.detail || 'Failed to save draft.' });
    } finally {
      setSaving(false);
    }
  };

  const generateInvoice = async (asProforma = false) => {
    if (!formData.company_id) {
      toast({ variant: 'destructive', title: 'Validation Error', description: 'Please select an Issuer Company.' });
      return;
    }
    if (!formData.client_name || formData.items.some(i => !i.description_en)) {
      toast({ variant: 'destructive', title: 'Validation Error', description: 'Please fill in all required fields.' });
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/finance/invoices/create-simple`,
        { ...formData, status: asProforma ? 'proforma' : 'issued' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setGeneratedInvoice(response.data.invoice);
        setSuccessDialog(true);
      }
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: error.response?.data?.detail || 'Failed to generate invoice.' });
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewPDF = () => {
    window.open(`${API_URL}/api/finance/invoices/${generatedInvoice.id}/pdf/preview?token=${token}`, '_blank');
  };

  const handleDownloadPDF = () => {
    window.open(`${API_URL}/api/finance/invoices/${generatedInvoice.id}/pdf/download?token=${token}`, '_blank');
  };

  const handleSendInvoice = async () => {
    try {
      await axios.post(`${API_URL}/api/finance/invoices/${generatedInvoice.id}/send`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Success', description: 'Invoice sent to client successfully.' });
      setSuccessDialog(false);
      navigate('/admin/finance/invoices');
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to send invoice.' });
    }
  };

  const handleSendTest = async () => {
    try {
      await axios.post(`${API_URL}/api/finance/invoices/${generatedInvoice.id}/send-test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Success', description: 'Test invoice sent to Bruno successfully.' });
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to send test invoice.' });
    }
  };

  const currencySymbols = { USD: '$', EUR: '\u20ac', GBP: '\u00a3', AED: 'AED ', AOA: 'AOA ' };

  return (
    <div className="p-6 space-y-6" data-testid="invoice-create-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/finance/invoices')} data-testid="back-to-invoices-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Create Invoice</h1>
          <p className="text-gray-600">Finance &gt; Invoices &gt; Create</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* ISSUER COMPANY - CRITICAL FIRST FIELD (P3-5 aligned) */}
          <div className="border-2 border-[#FF6B35] rounded-lg p-4 bg-[#FF6B35] bg-opacity-5" data-testid="issuer-company-section">
            <div className="flex items-center space-x-2 mb-3">
              <Building2 className="h-5 w-5 text-[#FF6B35]" />
              <label className="block text-sm font-bold text-[#2B2D42]">Issuer Company *</label>
            </div>
            <select
              required
              value={formData.company_id}
              onChange={(e) => {
                const id = e.target.value;
                setFormData(prev => ({ ...prev, company_id: id }));
                if (id) loadCompanySnapshot(id);
              }}
              className="w-full px-3 py-2 border-2 border-[#FF6B35] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B35] font-medium"
              data-testid="issuer-company-select"
            >
              <option value="">Select Issuer Company</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.trading_name || company.legal_name} ({company.country})
                </option>
              ))}
            </select>
            {companySnapshot && (
              <div className="mt-3 p-3 bg-white rounded border border-gray-200" data-testid="company-snapshot-preview">
                <div className="text-xs space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[#6C757D]">Legal Name:</span>
                    <span className="font-medium text-[#2B2D42]">{companySnapshot.legal_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#6C757D]">Country:</span>
                    <span className="font-medium text-[#2B2D42]">{companySnapshot.country}</span>
                  </div>
                  {companySnapshot.license_number && (
                    <div className="flex items-center justify-between">
                      <span className="text-[#6C757D]">License:</span>
                      <span className="font-medium text-[#2B2D42]">{companySnapshot.license_number}</span>
                    </div>
                  )}
                  {companySnapshot.trn && (
                    <div className="flex items-center justify-between">
                      <span className="text-[#6C757D]">TRN:</span>
                      <span className="font-medium text-[#2B2D42]">{companySnapshot.trn}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-[#6C757D]">Default Currency:</span>
                    <span className="font-bold text-[#FF6B35]">{formData.currency}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Client Information */}
          <Card>
            <CardHeader>
              <CardTitle>Client Information</CardTitle>
              <CardDescription>Select from master data to autofill — or enter manually for one-off clients.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="mb-2 block">Select Existing Customer (optional)</Label>
                <CustomerPicker
                  value={formData.customer_id}
                  onSelect={handleCustomerSelect}
                  onCreateNew={() => window.open('/admin/finance/customers', '_blank')}
                  testId="invoice-customer-picker"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Client Name *</Label>
                  <Input value={formData.client_name} onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    placeholder="Client Company Name" data-testid="client-name-input" />
                </div>
                <div>
                  <Label>Client Email</Label>
                  <Input type="email" value={formData.client_email} onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                    placeholder="client@example.com" data-testid="client-email-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Client Phone</Label>
                  <Input value={formData.client_phone} onChange={(e) => setFormData({...formData, client_phone: e.target.value})}
                    placeholder="+971 ..." data-testid="client-phone-input" />
                </div>
                <div>
                  <Label>Client Website</Label>
                  <Input value={formData.client_website} onChange={(e) => setFormData({...formData, client_website: e.target.value})}
                    placeholder="https://www.client.com" data-testid="client-website-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Client Address</Label>
                  <textarea className="w-full px-3 py-2 border rounded-md text-sm" rows="2"
                    value={formData.client_address} onChange={(e) => setFormData({...formData, client_address: e.target.value})}
                    placeholder="Street, City, Country" data-testid="client-address-input" />
                </div>
                <div>
                  <Label>Client Tax / Registration Identifier</Label>
                  <TaxIdInput
                    typeValue={formData.client_tax_id_type}
                    valueValue={formData.client_trn}
                    onTypeChange={(t) => setFormData({ ...formData, client_tax_id_type: t })}
                    onValueChange={(v) => setFormData({ ...formData, client_trn: v })}
                    country={formData.client_address}
                    testIdPrefix="client-tax-id"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Details */}
          <Card>
            <CardHeader><CardTitle>Invoice Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Issue Date</Label>
                  <Input type="date" value={formData.issue_date} onChange={(e) => setFormData({...formData, issue_date: e.target.value})} data-testid="issue-date-input" />
                </div>
                <div>
                  <Label>Due Date</Label>
                  <Input type="date" value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})} data-testid="due-date-input" />
                </div>
                <div>
                  <Label>Currency</Label>
                  <Select value={formData.currency} onValueChange={(v) => setFormData({
                    ...formData, currency: v,
                    // Reset dual-currency when primary changes
                    display_currency: null, fx_rate: null,
                    fx_rate_source: null, fx_rate_date: null,
                  })}>
                    <SelectTrigger data-testid="currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AED">AED (Default)</SelectItem>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Dual Currency Picker (Option D: Hybrid API + Manual Override) */}
              <DualCurrencyPicker
                primaryCurrency={formData.currency}
                displayCurrency={formData.display_currency}
                fxRate={formData.fx_rate}
                fxRateSource={formData.fx_rate_source}
                fxRateDate={formData.fx_rate_date}
                previewSubtotal={(formData.subtotal || 0) + (formData.tax_total || 0)}
                onChange={(patch) => setFormData((p) => ({ ...p, ...patch }))}
              />

              {/* Bank Account Selector (F-BankAccountOnInvoice) */}
              {companySnapshot && Array.isArray(companySnapshot.bank_accounts) && companySnapshot.bank_accounts.length > 0 && (
                <div className="mt-4 p-4 rounded-lg border border-slate-200 bg-slate-50/50" data-testid="bank-account-picker">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="font-semibold text-[#023E8A] uppercase text-xs tracking-wide">
                      Bank account on invoice
                    </Label>
                    <span className="text-[11px] text-[#6C757D]">
                      Auto-picked by currency match
                    </span>
                  </div>
                  <Select
                    value={formData.bank_account_id || ''}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, bank_account_id: v }))}
                  >
                    <SelectTrigger data-testid="bank-account-select">
                      <SelectValue placeholder="Select a bank account" />
                    </SelectTrigger>
                    <SelectContent>
                      {companySnapshot.bank_accounts.map((b) => (
                        <SelectItem key={b.id} value={b.id} data-testid={`bank-account-option-${b.id}`}>
                          {b.bank_name} • {b.currency || 'AED'}
                          {b.iban ? ` • IBAN ${b.iban.slice(0, 6)}…${b.iban.slice(-4)}` : ''}
                          {b.is_primary ? ' • PRIMARY' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(() => {
                    const sel = companySnapshot.bank_accounts.find(b => b.id === formData.bank_account_id);
                    if (!sel) return null;
                    return (
                      <div className="mt-2 text-xs text-[#6C757D] space-y-0.5" data-testid="bank-account-preview">
                        <div><b>{sel.bank_name}</b> ({sel.currency || 'AED'})</div>
                        {sel.iban && <div>IBAN: <code className="text-[#0EA5E9]">{sel.iban}</code></div>}
                        {sel.swift && <div>SWIFT: <code>{sel.swift}</code></div>}
                        {sel.account_number && <div>Account: <code>{sel.account_number}</code></div>}
                      </div>
                    );
                  })()}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Items */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Invoice Items</CardTitle>
                <Button size="sm" onClick={addItem} className="bg-[#00B4D8] hover:bg-[#0096c7] text-white" data-testid="add-item-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {formData.items.map((item, index) => (
                  <div key={index} className="border rounded-lg p-4 space-y-3" data-testid={`invoice-item-${index}`}>
                    <div className="flex justify-between items-start">
                      <h4 className="font-semibold text-sm">Item {index + 1}</h4>
                      {formData.items.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeItem(index)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs">Product from Master Registry (optional)</Label>
                      <ProductPicker
                        value={item.product_id || null}
                        onSelect={(p) => handleItemProductPick(index, p)}
                        placeholder="Search SKU or product name — auto-fills price & tax"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Description *</Label>
                      <Input value={item.description_en} onChange={(e) => handleItemChange(index, 'description_en', e.target.value)}
                        placeholder="Product or service description" data-testid={`item-description-${index}`} />
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs">Quantity</Label>
                        <Input type="number" min="1" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-xs">Unit Price</Label>
                        <Input type="number" min="0" step="0.01" value={item.unit_price} onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-xs">Tax (%)</Label>
                        <Select value={item.tax_rate.toString()} onValueChange={(v) => handleItemChange(index, 'tax_rate', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">0%</SelectItem>
                            <SelectItem value="5">5% (UAE VAT)</SelectItem>
                            <SelectItem value="10">10%</SelectItem>
                            <SelectItem value="15">15%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Total</Label>
                        <div className="px-3 py-2 bg-gray-50 rounded-md font-semibold text-sm">
                          {currencySymbols[formData.currency] || formData.currency + ' '}{item.total.toFixed(2)}
                        </div>
                      </div>
                    </div>
                    {!item.product_id && (item.description_en || '').trim().length >= 2 && (
                      <div className="pt-1">
                        <button
                          type="button"
                          onClick={() => setPromoteIdx(index)}
                          className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 transition"
                          title="Promote this ad-hoc item to the Master Product Registry for reuse"
                          data-testid={`promote-item-${index}`}
                        >
                          <Plus className="w-3 h-3" /> Save as Product / Service
                        </button>
                      </div>
                    )}
                    {item.product_id && item.product_sku && (
                      <div className="pt-1 text-[11px] text-[#00B4D8]">
                        Linked SKU: <code className="bg-white px-1 py-0.5 rounded border">{item.product_sku}</code>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Payment Options — Bank Transfer / Payment Link (Network) */}
          <Card>
            <CardHeader><CardTitle>Payment Options Offered</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { value: 'bank_transfer', label: 'Bank Transfer', hint: 'Uses the selected bank account snapshot.' },
                  { value: 'payment_link', label: 'Payment Link (card / wallet)', hint: 'Hosted checkout URL — Network, Stripe Payment Link, PayPal.me…' },
                ].map((opt) => {
                  const checked = (formData.payment_methods || []).includes(opt.value);
                  return (
                    <label
                      key={opt.value}
                      className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition ${
                        checked ? 'border-[#023E8A] bg-white shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid={`finance-pm-${opt.value}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        className="mt-0.5 accent-[#023E8A]"
                        onChange={(e) => {
                          const set = new Set(formData.payment_methods || []);
                          if (e.target.checked) set.add(opt.value); else set.delete(opt.value);
                          const arr = Array.from(set);
                          setFormData({ ...formData, payment_methods: arr.length ? arr : ['bank_transfer'] });
                        }}
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-[13px] font-bold text-[#2B2D42]">{opt.label}</span>
                        <span className="block text-[11px] text-[#6C757D] leading-snug">{opt.hint}</span>
                      </span>
                    </label>
                  );
                })}
              </div>

              {(formData.payment_methods || []).includes('payment_link') && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                  <div className="sm:col-span-2">
                    <Label>Payment link URL *</Label>
                    <Input
                      type="url"
                      value={formData.payment_link_url || ''}
                      onChange={(e) => setFormData({ ...formData, payment_link_url: e.target.value })}
                      placeholder="https://checkout.network.ae/…"
                      data-testid="finance-payment-link-url"
                    />
                    <div className="text-[10px] text-[#94a3b8] mt-1">
                      Paste the hosted checkout URL from{' '}
                      <a href="https://www.network.ae/en/merchant-solutions/ecommerce-payments" target="_blank" rel="noreferrer" className="text-[#023E8A] underline">
                        network.ae Merchant Solutions
                      </a>{' '}
                      (or any provider).
                    </div>
                  </div>
                  <div>
                    <Label>Provider</Label>
                    <Input
                      type="text"
                      value={formData.payment_link_provider || ''}
                      onChange={(e) => setFormData({ ...formData, payment_link_provider: e.target.value })}
                      placeholder="Network International"
                      data-testid="finance-payment-link-provider"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes & Terms */}
          <Card>
            <CardHeader><CardTitle>Notes & Terms</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Payment Terms</Label>
                  <div className="w-[280px]">
                    <PaymentTermPicker
                      currentValue={formData.terms}
                      onInsert={(v) => setFormData((p) => ({ ...p, terms: v }))}
                      mode="replace"
                      testId="invoice-payment-term-picker"
                    />
                  </div>
                </div>
                <Input value={formData.terms} onChange={(e) => setFormData({...formData, terms: e.target.value})} />
              </div>
              <div>
                <Label>Notes (Optional)</Label>
                <textarea className="w-full px-3 py-2 border rounded-md" rows="3"
                  value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Bank details or additional notes..." />
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={saveDraft} disabled={saving} data-testid="save-draft-btn">
              <Save className="w-4 h-4 mr-2" /> Save Draft
            </Button>
            <Button
              variant="outline"
              onClick={() => generateInvoice(true)}
              disabled={saving}
              className="border-amber-400 text-amber-700 hover:bg-amber-50"
              data-testid="generate-proforma-btn"
              title="Issue a Pro-Forma Invoice (not a fiscal document)"
            >
              <FileText className="w-4 h-4 mr-2" /> Generate Pro-Forma
            </Button>
            <Button onClick={() => generateInvoice(false)} disabled={saving} className="bg-[#00B4D8] hover:bg-[#0096c7] text-white" data-testid="generate-invoice-btn">
              {saving ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Generating...</>) : (<><FileText className="w-4 h-4 mr-2" />Generate Invoice</>)}
            </Button>
          </div>
        </div>

        {/* Live Preview */}
        <div className="lg:col-span-1">
          <Card className="sticky top-6">
            <CardHeader className="bg-gradient-to-r from-[#023E8A] to-[#012a5e] text-white">
              <CardTitle className="text-lg">Live Preview</CardTitle>
              <CardDescription className="text-gray-200">Real-time invoice preview</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {companySnapshot && (
                <div className="border-b pb-3">
                  <p className="text-xs text-gray-600">Issuer Company</p>
                  <p className="font-bold text-sm text-[#FF6B35]">{companySnapshot.trading_name || companySnapshot.legal_name}</p>
                  {companySnapshot.trn && <p className="text-xs text-gray-500">TRN: {companySnapshot.trn}</p>}
                  {companySnapshot.license_number && <p className="text-xs text-gray-500">License: {companySnapshot.license_number}</p>}
                  {companySnapshot.website && <p className="text-xs text-gray-500">Web: {companySnapshot.website}</p>}
                </div>
              )}
              <div>
                <p className="text-xs text-gray-600">Invoice Number</p>
                <p className="font-bold text-lg text-[#023E8A]">
                  {companySnapshot ? `AE-${(companySnapshot.trading_name || companySnapshot.legal_name || '').split(' ')[0].toUpperCase()}-INV-...` : 'Select Company First'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Client</p>
                <p className="font-semibold">{formData.client_name || 'N/A'}</p>
                {formData.client_trn && <p className="text-xs text-gray-500">TRN: {formData.client_trn}</p>}
                {formData.client_website && <p className="text-xs text-gray-500">Web: {formData.client_website}</p>}
                {formData.client_phone && <p className="text-xs text-gray-500">Tel: {formData.client_phone}</p>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><p className="text-xs text-gray-600">Issue Date</p><p className="text-sm">{formData.issue_date || 'N/A'}</p></div>
                <div><p className="text-xs text-gray-600">Due Date</p><p className="text-sm">{formData.due_date || 'N/A'}</p></div>
              </div>
              <div className="border-t pt-4">
                <p className="text-xs text-gray-600 mb-2">Items ({formData.items.length})</p>
                {formData.items.map((item, i) => (
                  <div key={i} className="text-xs flex justify-between">
                    <span className="truncate">{item.description_en || `Item ${i+1}`}</span>
                    <span className="font-semibold">{currencySymbols[formData.currency] || formData.currency + ' '}{item.total.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal:</span>
                  <span className="font-semibold">{currencySymbols[formData.currency] || formData.currency + ' '}{(formData.subtotal || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Taxes:</span>
                  <span className="font-semibold">{currencySymbols[formData.currency] || formData.currency + ' '}{(formData.tax_total || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-[#023E8A] border-t pt-2">
                  <span>Total:</span>
                  <span>{currencySymbols[formData.currency] || formData.currency + ' '}{(formData.total || 0).toFixed(2)}</span>
                </div>
                {formData.display_currency && formData.fx_rate && (
                  <div className="flex justify-between text-xs text-[#0EA5E9] pt-1"
                    data-testid="live-preview-dual-currency">
                    <span>≈ Equivalent:</span>
                    <span className="font-semibold">
                      {formData.display_currency}{' '}
                      {((formData.total || 0) * formData.fx_rate).toLocaleString(undefined, {
                        minimumFractionDigits: 2, maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                )}
                {formData.display_currency && formData.fx_rate && (
                  <p className="text-[10px] text-slate-400 leading-snug pt-1">
                    Legally in {formData.currency}. {formData.display_currency} is indicative —
                    frozen on {formData.fx_rate_date} ({formData.fx_rate_source}).
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Success Dialog */}
      <Dialog open={successDialog} onOpenChange={setSuccessDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#023E8A] text-xl">Invoice Generated Successfully</DialogTitle>
            <DialogDescription>
              Invoice <strong>#{generatedInvoice?.invoice_number}</strong> has been created.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 my-4">
            <Button variant="outline" onClick={handlePreviewPDF} className="justify-start" data-testid="preview-pdf-btn">
              <Eye className="w-4 h-4 mr-2" /> Preview PDF
            </Button>
            <Button variant="outline" onClick={handleDownloadPDF} className="justify-start" data-testid="download-pdf-btn">
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
            <Button variant="outline" onClick={handleSendTest} className="justify-start" data-testid="send-test-btn">
              <TestTube className="w-4 h-4 mr-2" /> Send Test Email to Bruno
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => navigate('/admin/finance/invoices')} data-testid="go-to-invoices-btn">Go to Invoices</Button>
            <Button onClick={handleSendInvoice} className="bg-[#00B4D8] hover:bg-[#0096c7] text-white" data-testid="send-to-client-btn">
              <Send className="w-4 h-4 mr-2" /> Send to Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PromoteItemToProductModal
        isOpen={promoteIdx !== null}
        item={promoteIdx !== null ? formData.items[promoteIdx] : null}
        currency={formData.currency}
        onClose={() => setPromoteIdx(null)}
        onPromoted={(product) => {
          if (promoteIdx === null) return;
          setFormData((prev) => ({
            ...prev,
            items: prev.items.map((it, i) => i === promoteIdx
              ? {
                  ...it,
                  product_id: product.id,
                  product_sku: product.sku,
                  description_en: it.description_en || product.name || '',
                }
              : it),
          }));
          setPromoteIdx(null);
        }}
      />
    </div>
  );
};

export default InvoiceCreate;
